import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    CreditCard as CreditCardIcon,
    Cancel as CancelIcon,
    Upgrade as UpgradeIcon,
    Receipt as ReceiptIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import { hostApi } from '../constants';
import { getStatusColor, formatDate } from '../../utils';

const SubscriptionManagement = () => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [cancelDialog, setCancelDialog] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        try {
            const formData = new FormData();
            formData.append('token', userStore.getToken());

            const response = await fetch(`${hostApi}subscription-status/`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setSubscription(data.subscription);
                setInvoices(data.invoices || []);
            } else {
                messageStore.setMessage(data.error || 'Failed to load subscription data', 'error');
            }
        } catch (err) {
            console.error('Subscription fetch error:', err);
            messageStore.setMessage('Failed to load subscription data. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('token', userStore.getToken());

            const response = await fetch(`${hostApi}cancel-subscription/`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setSubscription({ ...subscription, cancel_at_period_end: true });
                setCancelDialog(false);
                messageStore.setMessage('Subscription will be cancelled at the end of your billing period', 'success');
            } else {
                messageStore.setMessage(data.error || 'Failed to cancel subscription', 'error');
            }
        } catch (err) {
            console.error('Cancel subscription error:', err);
            messageStore.setMessage('Failed to cancel subscription. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdatePaymentMethod = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('token', userStore.getToken());

            const response = await fetch(`${hostApi}update-payment-method/`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                // This would typically redirect to Stripe's update payment method page
                messageStore.setMessage('Payment method update feature coming soon!', 'info');
            } else {
                messageStore.setMessage(data.error || 'Failed to update payment method', 'error');
            }
        } catch (err) {
            console.error('Update payment method error:', err);
            messageStore.setMessage('Failed to update payment method. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };


    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Loading subscription data...
                </Typography>
            </Container>
        );
    }

    if (!subscription || subscription.plan === 'free') {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Card sx={{ textAlign: 'center', p: 4 }}>
                    <CardContent>
                        <InfoIcon sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />
                        <Typography variant="h4" gutterBottom>
                            No Active Subscription
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            You're currently on the free plan. Upgrade to unlock premium features!
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<UpgradeIcon />}
                            href="/subscription/"
                            sx={{ mt: 2 }}
                        >
                            View Plans
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                Subscription Management
            </Typography>

            <Grid container spacing={4}>
                {/* Current Plan */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardHeader
                            title="Current Plan"
                            action={
                                <Chip
                                    label={subscription.status || subscription.plan}
                                    color={getStatusColor(subscription.status)}
                                    sx={{ textTransform: 'capitalize' }}
                                />
                            }
                        />
                        <CardContent>
                            <Typography variant="h4" color="primary" gutterBottom>
                                {subscription.plan} Plan
                            </Typography>

                            {subscription.is_active && (
                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckIcon color="success" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Next Billing Date"
                                            secondary={formatDate(subscription.current_period_end)}
                                        />
                                    </ListItem>
                                </List>
                            )}

                            {subscription.cancel_at_period_end && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                    <Typography variant="body2" color="warning.dark">
                                        <WarningIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                        Your subscription will be cancelled on {formatDate(subscription.current_period_end)}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Actions */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardHeader title="Manage Subscription" />
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CreditCardIcon />}
                                    onClick={handleUpdatePaymentMethod}
                                    disabled={actionLoading}
                                    fullWidth
                                >
                                    Update Payment Method
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<UpgradeIcon />}
                                    href="/subscription/"
                                    fullWidth
                                >
                                    Change Plan
                                </Button>

                                {!subscription.cancel_at_period_end && (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<CancelIcon />}
                                        onClick={() => setCancelDialog(true)}
                                        disabled={actionLoading}
                                        fullWidth
                                    >
                                        Cancel Subscription
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Cancel Confirmation Dialog */}
            <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel your subscription?
                        You'll continue to have access until the end of your current billing period.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialog(false)}>
                        Keep Subscription
                    </Button>
                    <Button
                        onClick={handleCancelSubscription}
                        color="warning"
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Cancel Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default SubscriptionManagement; 